// butterflies.js — butterflies flitting around the tree on clear summer days.
//
// The day-time counterpart to the fireflies: a small flock of GLB butterflies that
// wander loose orbits around the canopy, bobbing and banking as they go. Shown only
// when ALL hold (gates live in main.js's animate, eased here so the flock glides
// in/out):
//   • summer — not winter (leafType 'none'), not autumn (foliageColor 'autumn') and
//     not spring (cherry blossom or the Spring-Green palette), and
//   • clear skies — low cloud coverage (clearFactor), and
//   • day — the sun's day factor (dayFactor); they vanish at dusk as fireflies rise.
//
// butterfly-c.glb is a single static mesh (no bones, no clips), so there's nothing to
// play — we animate the transform ourselves: each butterfly flies a wandering path and
// "flaps" by oscillating its X scale, folding both wings toward the body centerline
// (the mesh is symmetric about X=0). Built once by main.js's initSky() (the GLB loads
// async and populates the flock); updateButterflies() drives it each frame.
//
// All randomness here is cosmetic (Math.random), never the seeded tree stream.

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

const MODEL_FILE = 'assets/models/butterfly-c.glb';

const BUTTERFLIES = {
  count:      2,
  wingspan:   6,    // world units across the wings (sets the model scale)
  radMin:     70,    // how close their orbits hug the trunk...
  radMax:     200,   // ...and how far out they range
  radJitter:  40,    // breathing in/out of the orbit radius
  centerJit:  60,    // each flock member orbits an offset centre, not the trunk
  yMin:       45,    // bob band: from mid-trunk...
  yMax:       175,   // ...up over the canopy
  yAmp:       22,    // vertical bob amplitude
  // Flap = a per-vertex fold of the wing X (see the shader inject): the outer wing
  // span is multiplied by this while the inner body (|x| < bodyHalf, model units)
  // stays rigid, so the body doesn't squash. Wider min→max = a more intense flap.
  flapMin:    0.05,  // wings nearly closed at the top of the upstroke
  flapMax:    1.10,  // wings fully spread (a touch past flat) at the bottom
  bodyHalf:   2.6,   // model-space half-width kept undeformed (pushes the fold outboard)
  fadeRate:   1.1,   // season-presence ease (~0.9s in/out)
  maxOpacity: 1.0,
};

// The GLB butterfly faces +Z in its own space, so heading = atan2(dir.x, dir.z) aims
// it down its travel direction. If they ever fly tail-first, flip this to Math.PI.
const FACING_YAW = 0;

function rand(a, b) { return a + Math.random() * (b - a); }

// Patch a material so it folds the wings about the body in the vertex shader: the
// outer wing span (|x| beyond bodyHalf) is multiplied by uFlap while the inner body
// stays at scale 1 — so flapping never squashes the body. Returns the uFlap uniform
// object so the caller can animate .value (the body half-width is baked in here).
function injectWingFold(m) {
  const uFlap = { value: 1 };
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uFlap = uFlap;
    shader.uniforms.uBodyHalf = { value: BUTTERFLIES.bodyHalf };
    shader.vertexShader = 'uniform float uFlap;\nuniform float uBodyHalf;\n' +
      shader.vertexShader.replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\n' +
        '  float ax = abs(transformed.x);\n' +
        '  transformed.x = sign(transformed.x) * (min(ax, uBodyHalf) + max(ax - uBodyHalf, 0.0) * uFlap);'
      );
  };
  return uFlap;
}

let group = null;       // parent of every butterfly; .visible mirrors the gate
let flock = [];         // per-butterfly state
let materials = [];     // every butterfly material, faded together for the gate
let presence = 0;       // eased season gate, 0 (hidden) → 1 (summer)
let _built = false;

/**
 * Build the flock. Call once; add the returned group to the scene. The GLB loads
 * asynchronously and populates the group when ready (empty until then).
 * @returns {THREE.Group}
 */
export function createButterflies() {
  group = new THREE.Group();
  group.visible = false;
  if (_built) return group;
  _built = true;

  new GLTFLoader().load(MODEL_FILE, (gltf) => {
    const src = gltf.scene;

    // Centre the mesh on its own origin so the flap-scale and yaw pivot about the
    // body, then scale to the target wingspan (X is the symmetric span axis).
    const box = new THREE.Box3().setFromObject(src);
    const size = new THREE.Vector3(); box.getSize(size);
    const center = new THREE.Vector3(); box.getCenter(center);
    const baseScale = BUTTERFLIES.wingspan / size.x;

    for (let i = 0; i < BUTTERFLIES.count; i++) {
      const model = src.clone(true);
      model.position.set(-center.x, -center.y, -center.z); // recentre to origin

      // Give each butterfly its own materials so the gate can fade the flock in/out
      // without one shared opacity tugging on the others, and keep them out of the
      // prune/shape raycast and the shadow pass (small flying accents). Each material
      // also gets the wing-fold shader inject; collect this butterfly's flap uniforms
      // so update() can drive them together.
      const flapUniforms = [];
      model.traverse((o) => {
        if (!o.isMesh) return;
        o.material = Array.isArray(o.material) ? o.material.map((m) => m.clone()) : o.material.clone();
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        for (const m of mats) {
          m.transparent = true; m.opacity = 0; materials.push(m);
          flapUniforms.push(injectWingFold(m));
        }
        o.castShadow = o.receiveShadow = false;
        o.frustumCulled = false; // they wander well past the rest pose's bounds
        o.raycast = () => {};
      });

      const s = baseScale * rand(0.8, 1.2);
      const root = new THREE.Group();
      root.add(model);
      root.scale.setScalar(s);

      flock.push({
        root, model, baseScale: s,
        cx: rand(-BUTTERFLIES.centerJit, BUTTERFLIES.centerJit),
        cz: rand(-BUTTERFLIES.centerJit, BUTTERFLIES.centerJit),
        ang:       rand(0, Math.PI * 2),
        angSpeed:  rand(0.15, 0.4) * (Math.random() < 0.5 ? -1 : 1), // orbit either way
        rad:       rand(BUTTERFLIES.radMin, BUTTERFLIES.radMax),
        radPhase:  rand(0, Math.PI * 2),
        radSpeed:  rand(0.2, 0.5),
        yBase:     rand(BUTTERFLIES.yMin, BUTTERFLIES.yMax),
        yPhase:    rand(0, Math.PI * 2),
        ySpeed:    rand(0.6, 1.3),
        flapPhase: rand(0, Math.PI * 2),
        flapSpeed: rand(10, 16),       // fast wing-beat
        flapUniforms,                  // this butterfly's per-material uFlap handles
        yaw:       rand(0, Math.PI * 2),
        px: 0, pz: 0,                  // previous position (seeded on first update)
        _seeded: false,
      });
      group.add(root);
    }
  }, undefined, (err) => console.error('[butterflies] failed to load GLB', err));

  return group;
}

/**
 * Drive the flock each frame from animate().
 * @param {number}  t           - performance.now()/1000 (flight + flap clock)
 * @param {number}  dt          - frame delta in seconds (for the season ease)
 * @param {number}  dayFactor   - 0 (night) → 1 (full day); butterflies are day-fliers
 * @param {number}  clearFactor - 0 (overcast) → 1 (clear skies)
 * @param {boolean} allowed     - summer: not winter, not autumn, not spring
 */
export function updateButterflies(t, dt, dayFactor, clearFactor, allowed) {
  if (!group) return;
  const target = allowed ? 1 : 0;
  presence += (target - presence) * Math.min(1, dt * BUTTERFLIES.fadeRate);

  const vis = presence * dayFactor * clearFactor * BUTTERFLIES.maxOpacity;
  group.visible = vis > 0.01;
  if (!group.visible) return;

  for (const m of materials) m.opacity = vis;

  for (const b of flock) {
    // Wandering orbit around an offset centre, breathing in and out, with a bob.
    b.ang += b.angSpeed * dt;
    const r = b.rad + Math.sin(t * b.radSpeed + b.radPhase) * BUTTERFLIES.radJitter;
    const x = b.cx + Math.cos(b.ang) * r;
    const z = b.cz + Math.sin(b.ang) * r;
    const y = b.yBase + Math.sin(t * b.ySpeed + b.yPhase) * BUTTERFLIES.yAmp;

    // Face the *actual* direction of travel: the radius-breathing adds a radial
    // velocity comparable to the tangential one, so the true heading wanders off the
    // orbit tangent (and at times runs inward/outward) — pointing at the analytic
    // tangent makes it look like it's flying backwards. Derive yaw from the real
    // frame delta instead, and only when it's actually moving (deadzone), then ease.
    const dx = x - b.px, dz = z - b.pz;
    if (b._seeded && dx * dx + dz * dz > 1e-4) {
      const goalYaw = Math.atan2(dx, dz) + FACING_YAW;
      let d = goalYaw - b.yaw;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      b.yaw += d * Math.min(1, dt * 4);
    }
    b.px = x; b.pz = z; b._seeded = true;

    b.root.position.set(x, y, z);
    b.root.rotation.y = b.yaw;

    // Flap: fold the outer wings about the body (per-vertex in the shader — see
    // injectWingFold), leaving the body rigid. Drive every material's uFlap together.
    const flap = BUTTERFLIES.flapMin + (BUTTERFLIES.flapMax - BUTTERFLIES.flapMin)
      * (0.5 + 0.5 * Math.sin(t * b.flapSpeed + b.flapPhase));
    for (const u of b.flapUniforms) u.value = flap;
  }
}
